import React, { useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Alert,
  APIActions,
  Box,
  Button,
  CustomDialog,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
  TABLE_INITIAL_STATE,
  Tooltip,
  Typography,
} from 'web-library';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { useDataTable } from 'web-library/stories/organism/DataTable/DataTable';
import { useHistory } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { stringToLongDateAndShortTime, stringToSemanticDisplay } from '../../../utils/DateUtils';
import DeleteButton from '../../../components/buttons/DeleteButton';
import {
  ListingResponse,
  specialConsiderationRequestModelList,
} from '../../../api-refactored/special-consideration/useGetCandidateListing';
import { useDeleteCandidateListing } from '../../../api-refactored/special-consideration/useDeleteCandidateListing';
import RouteKey from '../../../routes/key';

const { specialConsideration: specialConsiderationRoute } = RouteKey.child;
const { specialConsideration, instructions, applicationDetails } = RouteKey[
  'special-consideration'
].child;

const useStyles = makeStyles()((theme) =>
({
  tableSearchContainer: {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(2),
  },
  alert: {
    marginBottom: theme.spacing(3),
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  textDisabled: {
    color: theme.color.text.textDisabled,
  },
  buttonContainer: {
    display: 'inline-flex',
  },
  button: {
    margin: 'auto',
    padding: 0,
    width: 'max-content',
  },
  buttonFont: {
    '& span': {
      fontSize: '18px !important',
    },
  },
  paddingRight: {
    paddingRight: theme.spacing(6),
  },
  iconButtonContainer: {
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
  },
  iconRemove: {
    margin: 'auto',
  },
}));

enum ApplicationStatus {
  SUBMITTED = 'Submitted',
  DRAFT = 'Draft',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
}

const specialConsiderationAlert = (
  data: ListingResponse,
  classes: Record<string,string>,
  t: TFunction<'translation'>
) => {
  const { specialConsiderationRequestModelList: dataArray, errorMsg, canApplication } = data;
  // Display no exam registration error
  if (errorMsg === 'You have not registered for any examination.' && !canApplication) {
    return (
      <Alert
        className={classes.alert}
        variant="filled"
        severity="error"
        message={t('specialConsideration->>Listing.NoRegisteredExam')}
      />
    );
  }
  // Display types of exam level closed error
  const unavailableExamLevels = getUnavailableExamLevels(dataArray);

  if (unavailableExamLevels.length) {
    return (
      <Alert
        className={classes.alert}
        variant="filled"
        severity="error"
        message={t('specialConsideration->>Listing.Unavailable', {
          examLevels: unavailableExamLevels,
        })}
      />
    );
  }
  // Don't display any error if there is no draft
  return <></>;
};

const getUnavailableExamLevels = (
  specialConsiderations: specialConsiderationRequestModelList[]
) => {
  const unavailableExamLevels: string[] = [];

  specialConsiderations.forEach((specialConsiderationItem) => {
    if (
      specialConsiderationItem.applcationStatus === ApplicationStatus.DRAFT &&
      specialConsiderationItem.applicationClosed &&
      unavailableExamLevels.every(
        (unavailableExamLevel) => unavailableExamLevel !== specialConsiderationItem.examLevel
      )
    ) {
      unavailableExamLevels.push(specialConsiderationItem.examLevel);
    }
  });
  unavailableExamLevels.sort((a, b) => a.localeCompare(b));

  return unavailableExamLevels.join(', ');
};

const useTableColumns = (
  data: specialConsiderationRequestModelList[],
  handleOpenDeleteModal: (uuid: string) => void,
  t: TFunction,
  history: any,
  classes: Record<string,string>,
  theme: AugmentedTheme
): MUIDataTableColumnDef[] => [
    {
      name: 'applicationId',
      label: t('specialConsideration->>Listing.ApplicationId'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{data[dataIndex].applicationId}</Typography>
        ),
      },
    },
    {
      name: 'examLevel',
      label: t('specialConsideration->>Listing.ExamLevel'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{data[dataIndex].examLevel}</Typography>
        ),
      },
    },
    {
      name: 'applicationDateTime',
      label: t('specialConsideration->>Listing.ApplicationDateTime'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">
            {stringToLongDateAndShortTime(data[dataIndex].applicationDate)}
          </Typography>
        ),
      },
    },
    {
      name: 'status',
      label: t('specialConsideration->>Listing.Status'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          if (data[dataIndex].applcationStatus) {
            const { applcationStatus } = data[dataIndex];

            const checkApplicationStatus = () => {
              if (applcationStatus === ApplicationStatus.SUBMITTED) {
                return 'info';
              }
              if (applcationStatus === ApplicationStatus.APPROVED) {
                return 'positive';
              }
              if (applcationStatus === ApplicationStatus.REJECTED) {
                return 'negative';
              }
              return 'info';
            };

            return (
              <>
                <Pill variant={checkApplicationStatus()} label={data[dataIndex].applcationStatus} />
                <Box marginTop={1}>
                  <Typography variant="body2">
                    Last Updated: {stringToSemanticDisplay(data[dataIndex].lastUpdateDatetime)}
                  </Typography>
                </Box>
              </>
            );
          }
          return <></>;
        },
      },
    },
    {
      name: 'action',
      label: t('specialConsideration->>Listing.Action'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { paddingTop: '13px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={classes.buttonContainer}>
            {data[dataIndex].applcationStatus !== ApplicationStatus.DRAFT && (
              <>
                <Button
                  id={`button-specialConsideration-view-${dataIndex}`}
                  className={`${classes.button} ${classes.buttonFont}`}
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() =>
                    history.push(
                      `/${specialConsiderationRoute}/${specialConsideration.route}/${data[dataIndex].uuid}/${applicationDetails.route}`
                    )
                  }
                >
                  {t('specialConsideration->>Listing.View')}
                </Button>
              </>
            )}
            {data[dataIndex].applcationStatus === ApplicationStatus.DRAFT && (
              <Button
                id="button-specialConsideration-continue"
                className={`${classes.button} ${classes.paddingRight} ${classes.buttonFont}`}
                variant="text"
                color="primary"
                size="small"
                disabled={data[dataIndex].applicationClosed}
                onClick={() =>
                  history.push(
                    `/${specialConsiderationRoute}/${specialConsideration.route}/${instructions.route}`,
                    { isContinueApplication: true, applicationUuid: data[dataIndex].uuid }
                  )
                }
              >
                {t('specialConsideration->>Listing.Continue')}
              </Button>
            )}
            {data[dataIndex].applcationStatus === ApplicationStatus.DRAFT && (
              <DeleteButton
                deleteHandler={() => handleOpenDeleteModal(data[dataIndex].uuid)}
                label={t('specialConsideration->>Listing.Delete')}
              />
            )}
            {data[dataIndex].amendmentDraft && (
              <Tooltip
                arrow
                placement="bottom"
                title={<>{t('accessArrangement->>AccessArrangements.AmendmentDraft')}</>}
              >
                <IconButton size="large">
                  <Icon name={IconName.alertOff} color={theme.color.icon.iconDefault} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    },
  ];

interface SpecialConsiderationListingTableProps {
  data: ListingResponse;
  isFetching: boolean;
  refetch: () => void;
}

const SpecialConsiderationListingTable = ({
  data,
  isFetching,
  refetch,
}: SpecialConsiderationListingTableProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<string>('');

  const { state: tableState, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
  });

  const handleOpenDeleteModal = (uuid: string) => {
    setDeleteData(uuid);
    setOpenDeleteModal(true);
  };

  const tableColumns = useTableColumns(
    data.specialConsiderationRequestModelList,
    handleOpenDeleteModal,
    t,
    history,
    classes,
    theme
  );

  const deleteSpecialConsiderationRequest = useDeleteCandidateListing({
    onSuccess: refetch,
  });

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = () => {
    if (deleteData) {
      deleteSpecialConsiderationRequest.mutate(deleteData);
    }
    handleCloseDeleteModal();
  };

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
    }),
    [tOptions]
  );

  return (
    <Box>
      {data && specialConsiderationAlert(data, classes, t)}
      <MUIDataTable
        isLoading={isFetching}
        columns={tableColumns}
        data={data.specialConsiderationRequestModelList}
        dispatch={tableDispatch}
        state={tableState}
        options={customOptions}
        emptyDataLabel={t('specialConsideration->>Listing.EmptyDataLabel')}
      />
      <CustomDialog
        open={openDeleteModal}
        title={t('specialConsideration->>Listing.DeleteTitle')}
        message={t('specialConsideration->>Listing.DeleteMessage')}
        action={
          <APIActions
            onClose={handleCloseDeleteModal}
            apiCall={handleDelete}
            cancelString={t('specialConsideration->>Listing.Cancel')}
            apiString={t('specialConsideration->>Listing.Proceed')}
          />
        }
      />
    </Box>
  );
};

export default SpecialConsiderationListingTable;
